import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { PdfUploadService } from '../../../efile/components/pdf-upload/pdf-upload.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-dst-view-pdf-of-efile',
    templateUrl: './dst-view-pdf-of-efile.component.html',
    styleUrls: ['./dst-view-pdf-of-efile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DstViewPdfOfEfileComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  showFormLoader = true;
  filterParams: any = {};
  rowData: any = {};
  baseURLs: any = { admin: '', api: '', custom: '', efile: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  modalTitle = 'View PDF';
  errorMessage = '';

  private isInitialized = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private pdfService: PdfUploadService,
    private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = this.GENERAL_CONFIG;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG) return;
    this.isInitialized = true;
    this.openPdf();
    this.cdr.detectChanges();
  }

  private baseUrls(): GtwElementsBaseUrlsModel {
    const gc = this.GENERAL_CONFIG;
    const efile =
      gc?.gtw_efile_api_url ??
      gc?.efile ??
      this.baseURLs?.efile ??
      '';
    return {
      api: gc?.api ?? '',
      admin: gc?.admin ?? '',
      custom: gc?.custom ?? '',
      efile,
      pdfEngine: gc?.pdfEngine ?? '',
      bulkPdfEngine: gc?.bulkPdfEngine ?? '',
    };
  }

  private openPdf(): void {
    const submissionId = this.filterParams?.submission_id || this.rowData?.SUBMISSION_ID;
    const attachmentName = this.rowData?.ATTACHMENT_NAME;
    if (!this.rowData?.COMMENTS || !attachmentName || !submissionId) {
      this.errorMessage = 'Push the selected file to Efile before viewing the PDF.';
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }

    this.subs.add(
      this.pdfService.viewPdf(this.baseUrls(), submissionId, attachmentName).subscribe({
        next: (data: ArrayBuffer) => {
          const file = new Blob([data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          this.showFormLoader = false;
          this.cdr.detectChanges();
          window.open(fileURL, '_blank');
          this.requestGridUpdate.emit('cancel');
        },
        error: () => {
          this.errorMessage = 'Something went wrong! Please try again.';
          this.showFormLoader = false;
          this.gtwSnackbar.openSnackBar(this.errorMessage, 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      }),
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
